import { useState } from "react";
import { Plus, Loader2, MapPin, Flag, X, CheckCircle2, Share2 } from "lucide-react";
import toast from "react-hot-toast";
import { useTrips } from "../hooks/useTrips";
import NewTripModal from "./NewTripModal";
import TripShareModal from "./TripShareModal";

const STATUS_STYLE = {
  planned:     { label: "Planned",     color: "#6B7280", bg: "#F3F4F6" },
  in_progress: { label: "In Progress", color: "#D97706", bg: "#FFFBEB" },
  completed:   { label: "Completed",   color: "#16A34A", bg: "#F0FDF4" },
  cancelled:   { label: "Cancelled",   color: "#9CA3AF", bg: "#F9FAFB" },
};

function fmtDateTime(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleString("en-IN", {
    day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hour12: true,
  });
}

export default function TripsPanel({ vehicles }) {
  const { trips, loading, createTrip, cancelTrip, markStarted, markCompleted } = useTrips();
  const [showForm, setShowForm]   = useState(false);
  const [shareTrip, setShareTrip] = useState(null);

  async function handleCancel(trip) {
    if (!window.confirm("Cancel this trip?")) return;
    try {
      await cancelTrip(trip.id);
      toast.success("Trip cancelled");
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to cancel trip");
    }
  }

  async function handleMarkStarted(trip) {
    try {
      await markStarted(trip.id);
      toast.success("Marked as started");
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to update trip");
    }
  }

  async function handleMarkCompleted(trip) {
    try {
      await markCompleted(trip.id);
      toast.success("Marked as completed");
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to update trip");
    }
  }

  function vehicleLabel(vehicleId) {
    const v = vehicles.find(v => v.id === vehicleId);
    return v ? (v.nickname || v.plateNumber) : "Unknown vehicle";
  }

  return (
    <main style={{ flex: 1, overflow: "auto", background: "#F9FAFB" }}>
      <div style={{ maxWidth: 900, margin: "0 auto", padding: "28px 24px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#111827" }}>Trips</div>
            <div style={{ fontSize: 13, color: "#6B7280", marginTop: 2 }}>
              Auto-detected from FASTag toll crossings — no driver input needed
            </div>
          </div>
          <button
            onClick={() => setShowForm(true)}
            disabled={!vehicles.length}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              background: vehicles.length ? "#4F46E5" : "#D1D5DB", border: "none", borderRadius: 8,
              padding: "0 16px", height: 38, cursor: vehicles.length ? "pointer" : "default",
              fontSize: 13.5, fontWeight: 700, color: "#fff",
            }}
          >
            <Plus size={15} /> New Trip
          </button>
        </div>

        {loading && (
          <div style={{ padding: 60, textAlign: "center", color: "#9CA3AF", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            <Loader2 size={18} style={{ animation: "spin 1s linear infinite" }} /> Loading trips…
          </div>
        )}

        {!loading && trips.length === 0 && (
          <div style={{
            background: "#fff", border: "1px dashed #D1D5DB", borderRadius: 14,
            padding: "48px 24px", textAlign: "center",
          }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: "#374151", marginBottom: 6 }}>No trips yet</div>
            <div style={{ fontSize: 13, color: "#9CA3AF", marginBottom: 18 }}>
              Create a trip with a source and destination — it'll auto-advance as the vehicle crosses tolls near each point.
            </div>
            {!!vehicles.length && (
              <button
                onClick={() => setShowForm(true)}
                style={{ background: "#4F46E5", border: "none", borderRadius: 8, padding: "0 18px", height: 38, color: "#fff", fontSize: 13.5, fontWeight: 700, cursor: "pointer" }}
              >
                <Plus size={14} style={{ marginRight: 6, verticalAlign: -2 }} /> New Trip
              </button>
            )}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {trips.map(trip => {
            const st = STATUS_STYLE[trip.status] || STATUS_STYLE.planned;
            return (
              <div key={trip.id} style={{
                background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12,
                padding: "16px 18px", display: "flex", flexDirection: "column", gap: 10,
              }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                  <div>
                    <div style={{ fontSize: 14.5, fontWeight: 700, color: "#111827" }}>
                      {trip.label || vehicleLabel(trip.vehicleId)}
                    </div>
                    <div style={{ fontSize: 12, color: "#6B7280", marginTop: 2 }}>{vehicleLabel(trip.vehicleId)}</div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{
                      fontSize: 11, fontWeight: 700, color: st.color, background: st.bg,
                      borderRadius: 99, padding: "3px 10px",
                    }}>
                      {st.label}
                    </span>
                    <button
                      onClick={() => setShareTrip(trip)}
                      title="Share shipment status"
                      style={{ display: "flex", alignItems: "center", gap: 5, background: "none", border: "1px solid #E5E7EB", borderRadius: 7, padding: "6px 10px", fontSize: 12, fontWeight: 600, color: "#6B7280", cursor: "pointer" }}
                    ><Share2 size={12} /> Share</button>
                  </div>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: "#374151", flexWrap: "wrap" }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <MapPin size={12} color="#16A34A" /> {trip.source.label || `${trip.source.lat.toFixed(3)}, ${trip.source.lng.toFixed(3)}`}
                  </span>
                  <span style={{ color: "#D1D5DB" }}>→</span>
                  <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <Flag size={12} color="#DC2626" /> {trip.destination.label || `${trip.destination.lat.toFixed(3)}, ${trip.destination.lng.toFixed(3)}`}
                  </span>
                </div>

                {(trip.startedAt || trip.completedAt) && (
                  <div style={{ display: "flex", gap: 16, fontSize: 11.5, color: "#9CA3AF", flexWrap: "wrap" }}>
                    {trip.startedAt && (
                      <span>Started {fmtDateTime(trip.startedAt)}{trip.startedTollPlaza ? ` · ${trip.startedTollPlaza}` : ""}</span>
                    )}
                    {trip.completedAt && (
                      <span>Completed {fmtDateTime(trip.completedAt)}{trip.completedTollPlaza ? ` · ${trip.completedTollPlaza}` : ""}</span>
                    )}
                    {trip.manualOverride && <span style={{ color: "#D97706", fontWeight: 600 }}>Manually marked</span>}
                  </div>
                )}

                {(trip.status === "planned" || trip.status === "in_progress") && (
                  <div style={{ display: "flex", gap: 8, marginTop: 2 }}>
                    {trip.status === "planned" && (
                      <button
                        onClick={() => handleMarkStarted(trip)}
                        style={{ display: "flex", alignItems: "center", gap: 5, background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 7, padding: "6px 12px", fontSize: 12, fontWeight: 600, color: "#D97706", cursor: "pointer" }}
                      ><CheckCircle2 size={12} /> Mark as Started</button>
                    )}
                    {trip.status === "in_progress" && (
                      <button
                        onClick={() => handleMarkCompleted(trip)}
                        style={{ display: "flex", alignItems: "center", gap: 5, background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 7, padding: "6px 12px", fontSize: 12, fontWeight: 600, color: "#16A34A", cursor: "pointer" }}
                      ><CheckCircle2 size={12} /> Mark as Completed</button>
                    )}
                    <button
                      onClick={() => handleCancel(trip)}
                      style={{ display: "flex", alignItems: "center", gap: 5, background: "none", border: "1px solid #FECACA", borderRadius: 7, padding: "6px 12px", fontSize: 12, fontWeight: 600, color: "#DC2626", cursor: "pointer" }}
                    ><X size={12} /> Cancel</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {showForm && (
        <NewTripModal
          vehicles={vehicles}
          onClose={() => setShowForm(false)}
          onSubmit={createTrip}
        />
      )}

      {shareTrip && (
        <TripShareModal
          trip={shareTrip}
          onClose={() => setShareTrip(null)}
        />
      )}
    </main>
  );
}
